import { useEffect, useRef } from 'react'
import { CircleCheckBig, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from '@/i18n'
import SortableHeader from './SortableHeader'
import TableSkeletonRows from './TableSkeletonRows'
import type { TagMappingSort } from './sort'
import type { PendingTagRow } from '@/service/api'
import type { EditMappingTarget } from './EditMappingDialog'
import MatchBadge from './MatchBadge'
import RowActionsMenu, { type RowAction } from './RowActionsMenu'

interface Props {
  rows: PendingTagRow[]
  loading: boolean
  /** The load in flight will REPLACE these rows — see MatchedTable (review F10 / G3). */
  reloading?: boolean
  hasMore: boolean
  searching: boolean
  onLoadMore: () => void
  onSelectTag: (tag: string, source?: PendingTagRow['source']) => void
  /**
   * Open the row's Source file in the board's document preview, searching for
   * the tag. Optional: without it the filename renders as text.
   */
  onSelectSourceFile?: (
    documentId: string,
    documentName: string,
    sourceType?: string | null,
    highlightTag?: string,
  ) => void
  /** Accept the row's proposed mapping as it stands. */
  onApprove?: (row: PendingTagRow) => void
  /** Open the picker to choose a different target. */
  onEditMapping?: (target: EditMappingTarget) => void
  /** Take the row out of review. */
  onDelete?: (row: PendingTagRow) => void
  /**
   * A server facet (source, sheet, board) is narrowing the page. An empty page
   * then reads "nothing matches these filters", not "every tag has a match"
   * (Tag Mapping review 2026-09-28, D1).
   */
  filtered?: boolean
  /** Drops every facet. Rendered as the empty state's action when `filtered`. */
  onClearFilters?: () => void
  /**
   * The active column sort, applied by the SERVER (review F09). Optional with
   * `onSort`: a caller that does not sort renders plain-text headers.
   */
  sort?: TagMappingSort | null
  onSort?: (key: string) => void
}

/**
 * Pending tab: tags with no saved match.
 *
 * Deliberately the SAME six columns as Matched, in the same order and at the
 * same widths — frame 1232:285869 draws the two tabs identically and differs
 * only in the scores and in what the kebab offers.
 */
const PendingTable = ({
  rows,
  loading,
  reloading = false,
  hasMore,
  searching,
  onLoadMore,
  onSelectTag,
  onSelectSourceFile,
  onApprove,
  onEditMapping,
  onDelete,
  filtered = false,
  onClearFilters,
  sort = null,
  onSort,
}: Props) => {
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hasMore) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore()
      },
      { rootMargin: '200px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [onLoadMore, hasMore])

  /**
   * A ruled-out row is GONE, not struck through. The server keeps a decided
   * row in its place, so it arrives with its verdict on every page; this used
   * to draw it at half opacity with a line through it, which Pratima read as a
   * row still sitting there: "we should remove the entry we shouldnt keep them
   * as deleted putting a cut". Filtered here rather than upstream so the page
   * arithmetic keeps counting what the server sent.
   */
  const visible = rows.filter((row) => row.verdict !== 'not_a_tag')

  // Empty only when there is nothing more to load: a page made entirely of
  // decided rows is not "nothing to review", it is "keep scrolling".
  if (!loading && visible.length === 0 && !hasMore) {
    return (
      <div className="px-6 py-16 text-center text-sm text-muted-foreground">
        {searching
          ? t('data_catalog:tag_mapping.empty.no_results', {
              defaultValue: 'No tags match your search.',
            })
          : filtered
            ? t('data_catalog:tag_mapping.empty.no_pending_for_filters', {
                defaultValue: 'No pending tags match these filters.',
              })
            : t('data_catalog:tag_mapping.empty.pending', {
                defaultValue: 'Nothing pending — every tag has a match.',
              })}
        {!searching && filtered && onClearFilters && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onClearFilters}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              {t('data_catalog:tag_mapping.empty.clear_filters', { defaultValue: 'Clear filters' })}
            </button>
          </div>
        )}
      </div>
    )
  }

  /**
   * `documents.type` as a label. Reuses the catalog's own file-type vocabulary
   * rather than a second one private to this table, so "Time Series" here and
   * in the Data Catalog filter cannot drift apart.
   */
  const sourceTypeLabel = (type?: string | null): string => {
    if (!type) return '—'
    const key: string = `data_catalog:doc_types.${type.toLowerCase()}`
    return t(key, { defaultValue: type })
  }

  /**
   * The kebab's items, and there are TWO menus, not one shape with a greyed
   * item — frame 1328:235270 draws a 0% row's menu as exactly "Map" and
   * "Delete", with no Approve and no Edit.
   *
   * The split is the confidence: `candidate_tag` is null exactly when the
   * matcher scored below PENDING_CANDIDATE_MIN_CONFIDENCE, which is the row
   * that reads 0% with an en-dash for its mapped tag. Nothing has been
   * proposed for it, so there is nothing to approve and nothing to edit — the
   * reviewer's job there is to CHOOSE a counterpart, which is what Map does.
   * A scored row has a proposal, so it gets Approve / Edit / Delete (frame
   * 1232:286147).
   *
   * Map and Approve share the circle-check glyph in both frames: both are the
   * affirmative action of their menu, one picking a counterpart and the other
   * accepting the picked one.
   */
  const rowActions = (row: PendingTagRow): RowAction[] => {
    const actions: RowAction[] = []
    // A row with no proposal at all. It reads 0% in the Confidence column, and
    // its menu is Map + Delete.
    const unproposed = !row.candidate_tag

    if (unproposed) {
      if (onEditMapping) {
        actions.push({
          key: 'map',
          label: t('data_catalog:tag_mapping.row_actions.map', { defaultValue: 'Map' }),
          icon: <CircleCheckBig aria-hidden />,
          onSelect: () =>
            onEditMapping({
              tag: row.tag,
              displayTag: row.display_tag,
              // Same rule as Edit below: only a DRAWING's id may travel as the
              // match's `pid_document_id`.
              documentId: row.source === 'sheet' ? (row.source_document_id ?? null) : null,
              // Nothing to replace: the dialog titles itself as a creation.
              mode: 'create',
            }),
        })
      }
      if (onDelete) {
        actions.push({
          key: 'delete',
          label: t('data_catalog:tag_mapping.row_actions.delete', { defaultValue: 'Delete' }),
          icon: <Trash2 aria-hidden />,
          disabled: row.verdict === 'not_a_tag',
          onSelect: () => onDelete(row),
        })
      }
      return actions
    }

    if (onApprove) {
      actions.push({
        key: 'approve',
        label: t('data_catalog:tag_mapping.row_actions.approve', {
          defaultValue: 'Approve',
        }),
        icon: <CircleCheckBig aria-hidden />,
        // No `disabled` branch: a row with nothing to approve never reaches
        // here any more, it took the Map menu above. Greying the item out was
        // the previous reading of the frames — 1328:235270 shows the whole
        // menu differs, not one item's state.
        //
        // NOT gated on `source`. The review's finding was the misattribution,
        // not the action: a prose row's document must not travel as the
        // match's `pid_document_id`. That is fixed where it belongs, at the
        // write (see handleApprovePending), so a prose row whose candidate did
        // clear the bar can still be approved -- which is what the frame draws.
        onSelect: () => onApprove(row),
      })
    }
    if (onEditMapping) {
      actions.push({
        key: 'edit',
        label: t('data_catalog:tag_mapping.row_actions.edit', { defaultValue: 'Edit' }),
        icon: <Pencil aria-hidden />,
        onSelect: () =>
          onEditMapping({
            // The RAW parse content is the identity the write is keyed on;
            // `display_tag` is only what the cell shows.
            tag: row.tag,
            displayTag: row.display_tag,
            // Only a DRAWING's id may travel as the match's `pid_document_id`
            // -- the same misattribution the review caught on Approve. On a
            // prose row `source_document_id` is a document, and `connect`
            // stores whatever it is given. Null rather than wrong: the match
            // is then saved with NO drawing attribution, which is the truth.
            documentId: row.source === 'sheet' ? (row.source_document_id ?? null) : null,
          }),
      })
    }
    if (onDelete) {
      actions.push({
        key: 'delete',
        label: t('data_catalog:tag_mapping.row_actions.delete', { defaultValue: 'Delete' }),
        icon: <Trash2 aria-hidden />,
        // The row does NOT disappear -- the server keeps a decided row in
        // place so the reviewer can see the decision land and revise it, and
        // this table now draws it as decided. Repeating the verdict would
        // change nothing, so the item goes inert once it is recorded.
        disabled: row.verdict === 'not_a_tag',
        onSelect: () => onDelete(row),
      })
    }
    return actions
  }

  const coldLoad = loading && visible.length === 0
  const updating = reloading && visible.length > 0

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      {/* Screen-reader only: sighted users see the rows dim. A visible
          "Updating…" line on every sort click was noise. */}
      {updating && (
        <p role="status" aria-live="polite" className="sr-only">
          {t('data_catalog:tag_mapping.table.updating', { defaultValue: 'Updating…' })}
        </p>
      )}
      <table className="w-full table-fixed text-sm">
        <colgroup>
          {/* Matched's spec exactly, so switching tabs does not shift the grid.
              Source type / Confidence / Actions are FIXED px -- the frame keeps
              them at 191 / 165 / 46 whether the Data sidebar is open (content
              1072) or closed (1328) -- and Source file takes the remainder.
              See MatchedTable for the arithmetic. */}
          <col className="w-[15%]" />
          <col className="w-[15%]" />
          <col />
          <col className="w-[191px]" />
          <col className="w-[165px]" />
          <col className="w-[46px]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-background dark:bg-dark-card">
          <tr className="border-b border-border text-left text-sm text-muted-foreground">
            {/* Review F09 / B1: all five headings sort the WHOLE filtered set
                (`PENDING_SORT_KEYS`). Mapped tag and Confidence make the server
                rank a candidate for every row first, see sort.ts. */}
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.pid_tag', { defaultValue: 'P&ID tag' })}
              sortKey="pid_tag"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.mapped_tag', { defaultValue: 'Mapped tag' })}
              sortKey="mapped_tag"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.source_file', { defaultValue: 'Source file' })}
              sortKey="source_file"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.source_type', { defaultValue: 'Source type' })}
              sortKey="source_type"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.confidence', { defaultValue: 'Confidence' })}
              sortKey="confidence"
              sort={sort}
              onSort={onSort}
            />
            {/* Unlabelled in the frame; the header still exists so the column
                count matches and screen readers get a name. */}
            <th className="px-2 py-3 font-normal">
              <span className="sr-only">
                {t('data_catalog:tag_mapping.column.actions', { defaultValue: 'Actions' })}
              </span>
            </th>
          </tr>
        </thead>
        <tbody className={updating ? 'opacity-60 transition-opacity' : undefined} aria-busy={updating || undefined}>
          {coldLoad && <TableSkeletonRows />}
          {visible.map((row) => {
            return (
            <tr
              key={row.tag}
              className="group/row border-b border-border hover:bg-accent"
            >
              <td className="truncate px-6 py-3">
                <button
                  type="button"
                  onClick={() => onSelectTag(row.tag, row.source)}
                  // The raw parse content can be a long merged blob; the title
                  // makes it inspectable without widening the column.
                  title={row.tag}
                  className="max-w-full truncate text-left text-sm text-blue-600 hover:underline dark:text-blue-400"
                >
                  {row.display_tag}
                </button>
              </td>
              <td className="truncate px-6 py-3">
                {/* Plain text, as on Matched (Pegah, 2026-10-13 review): Source
                    file is the one link to the file. */}
                {row.candidate_tag ? (
                  <span className="text-sm text-foreground" title={row.candidate_tag}>
                    {row.candidate_tag}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">–</span>
                )}
              </td>
              <td
                className="truncate px-6 py-3 text-foreground"
                title={row.source_document_name ?? undefined}
              >
                {row.source_document_id && row.source_document_name && onSelectSourceFile ? (
                  <button
                    type="button"
                    onClick={() =>
                      onSelectSourceFile(
                        row.source_document_id as string,
                        row.source_document_name as string,
                        row.source_type,
                        // `display_tag`, not `tag`: the raw parse content can
                        // be a merged blob no search would find.
                        row.display_tag,
                      )
                    }
                    className="max-w-full truncate text-left text-sm text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {row.source_document_name}
                  </button>
                ) : (
                  (row.source_document_name ?? '—')
                )}
              </td>
              <td className="truncate px-6 py-3 text-foreground">
                {sourceTypeLabel(row.source_type)}
              </td>
              <td className="px-6 py-3">
                <MatchBadge confidence={row.confidence} />
              </td>
              <td className="px-2 py-3">
                <RowActionsMenu
                  label={t('data_catalog:tag_mapping.row_actions.label', {
                    defaultValue: 'Row actions',
                  })}
                  actions={rowActions(row)}
                />
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>

      {/* Spinner only for a page appending below rows already on screen. */}
      {loading && !coldLoad && !updating && (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}
      {hasMore && <div ref={sentinelRef} className="h-1" />}
    </div>
  )
}

export default PendingTable
